import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './MobileBottomBar.css';

/**
 * App-wide bottom icon bar for the mobile shell (AuthenticatedLayout ≤768px),
 * in the Instagram pattern: fixed to the screen's bottom edge, icons spread
 * evenly. The button set is DYNAMIC per route — the bar decides its own
 * variant from the URL so screens don't have to manage it:
 *
 *   /projects (the list)  →  [search] [refresh] [menu]
 *   everywhere else       →  [back]   [menu]
 *
 * The menu lives here (moved down from MobileTopBar), so it must exist in
 * every variant — it is the only way into the drawer.
 */
const MobileBottomBar = ({ onMenuClick, onSearchClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isProjectsList = location.pathname === '/projects';

  return (
    <nav className='mobile-bottom-bar' aria-label='Primary'>
      {/* Back is the inverse of the search/refresh pair: it exists exactly
          where the list doesn't, so the bar never shows a "back" on the screen
          that IS the destination.

          navigate('/projects'), not navigate(-1): history back is only
          equivalent when the user arrived from the list. On a deep link, a
          refresh, or after the drawer's own history entries (see
          useDrawerBackNavigation), -1 lands somewhere arbitrary or leaves the
          app entirely. A fixed destination is what the button promises.

          Relative navigation also keeps this a client-side transition — the
          projects tree stays mounted rather than reloading the document. */}
      {!isProjectsList && (
        <button
          type='button'
          className='mobile-bottom-bar__item'
          onClick={() => navigate('/projects')}
          aria-label='Back to projects'
        >
          <i className='fas fa-arrow-left' aria-hidden='true'></i>
        </button>
      )}
      {isProjectsList && (
        <button
          type='button'
          className='mobile-bottom-bar__item'
          onClick={onSearchClick}
          aria-label='Search projects'
        >
          <i className='fas fa-magnifying-glass' aria-hidden='true'></i>
        </button>
      )}
      {isProjectsList && (
        <button
          type='button'
          className='mobile-bottom-bar__item'
          onClick={() =>
            window.dispatchEvent(new CustomEvent('rsa:mobile-refresh'))
          }
          aria-label='Refresh projects'
        >
          <i className='fas fa-rotate-right' aria-hidden='true'></i>
        </button>
      )}
      <button
        type='button'
        className='mobile-bottom-bar__item'
        onClick={onMenuClick}
        aria-label='Open menu'
      >
        <i className='fas fa-bars' aria-hidden='true'></i>
      </button>
    </nav>
  );
};

export default MobileBottomBar;
